import type { DataOption } from '@/common.types'
import type { PricingCampaignModel } from '@/shared/generated'

import { Fragment, useCallback, useMemo, useState } from 'react'
import { difference, find, map, pipe, prop, propEq, sortBy } from 'lodash/fp'
import { ListFilterIcon, LoaderCircleIcon, SearchIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import {
	useGlobalPcSetsQuery,
	usePricingCampaignsQuery,
} from '@/entities/pricing-campaign'

import { Button } from '@/shared/components/ui/button'
import { Checkbox } from '@/shared/components/ui/checkbox-native'
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from '@/shared/components/ui/command'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu'
import {
	InputGroup,
	InputGroupAddon,
	InputGroupButton,
	InputGroupInput,
} from '@/shared/components/ui/input-group'
import { Separator } from '@/shared/components/ui/separator'
import {
	Virtualized,
	VirtualizedVirtualizer,
} from '@/shared/components/ui/virtualizer'

import { useGlobalPcSets } from './GlobalPcSetsProvider'

export default function SearchFilterField() {
	const { t } = useTranslation()

	const { setSearchText, searchText, selectedPcIds, setSelectedPcIds } =
		useGlobalPcSets()
	const [selected, setSelected] = useState<string[]>(selectedPcIds)
	const [isOpen, setIsOpen] = useState(false)
	const { data: globalSets, isLoading: isGlobalSetsLoading } =
		useGlobalPcSetsQuery()
	const { data: pricingCampaignsOptions, isLoading: isOptionsLoading } =
		usePricingCampaignsQuery<(DataOption & { keywords: string[] })[]>(
			{
				only_global: true,
				only_main_global: false,
			},
			{
				enabled: !!globalSets,
				select: pipe(
					sortBy([({ name }) => name.toLowerCase()]),
					map((pc: PricingCampaignModel) => {
						const globalSetName = pipe(
							find(propEq('id', pc.global_pc_set_id)),
							prop('name')
						)(globalSets)

						return {
							value: pc.id,
							label: `${pc.name} - ${globalSetName}`,
							keywords: [pc.name],
						}
					})
				),
			}
		)
	const handleApply = useCallback(() => {
		setSelectedPcIds(selected)
		setIsOpen(false)
	}, [selected, setSelectedPcIds])
	const handlePcSelect = (value: string) => {
		setSelected((prev) =>
			prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
		)
	}
	const isApplyDisabled = useMemo(
		() =>
			selected.length !== selectedPcIds.length
				? false
				: difference(selected, selectedPcIds).length === 0,
		[selected, selectedPcIds]
	)
	const sortedOptions: (DataOption & { keywords: string[] })[] = useMemo(() => {
		if (!pricingCampaignsOptions) return []
		if (!selectedPcIds.length) return pricingCampaignsOptions

		return pipe(
			sortBy([
				(option: DataOption) => (selectedPcIds.includes(option.value) ? 0 : 1),
			])
		)(pricingCampaignsOptions)
	}, [pricingCampaignsOptions, selectedPcIds])
	const isLoading = isOptionsLoading || isGlobalSetsLoading

	return (
		<InputGroup>
			<InputGroupAddon>
				<SearchIcon />
			</InputGroupAddon>
			<InputGroupInput
				disabled={isLoading}
				placeholder={t('general_search')}
				value={searchText}
				onChange={(e) => {
					setSearchText(e.target.value)
				}}
			/>
			<InputGroupAddon align='inline-end'>
				<DropdownMenu
					open={isOpen}
					onOpenChange={(open) => {
						setIsOpen(open)

						if (!open) {
							setSelected(selectedPcIds)
						}
					}}
				>
					<DropdownMenuTrigger asChild disabled={isLoading}>
						<InputGroupButton size='icon-xs' className='relative'>
							{isLoading ? (
								<LoaderCircleIcon className='size-4 animate-spin' />
							) : (
								<Fragment>
									<ListFilterIcon />
									{selectedPcIds.length ? (
										<span className='absolute top-0.5 right-0.5 size-2 shrink-0 rounded-full bg-primary' />
									) : null}
								</Fragment>
							)}
						</InputGroupButton>
					</DropdownMenuTrigger>
					<DropdownMenuContent align='start' className='space-y-1 p-0'>
						<Command className='w-96'>
							<CommandInput placeholder={t('general_search')} />
							<Virtualized asChild>
								<CommandList>
									<CommandEmpty>
										{t('no_entity_found', { entity: t('optimization_groups') })}
									</CommandEmpty>
									<CommandGroup>
										<VirtualizedVirtualizer>
											{sortedOptions?.map(({ value, label, keywords }) => {
												const isChecked = selected.includes(value)

												return (
													<CommandItem
														data-value={value}
														value={value}
														key={value}
														keywords={keywords}
														className='cursor-text'
													>
														<Checkbox
															checked={isChecked}
															onChange={() => handlePcSelect(value)}
														/>
														<span>{label}</span>
													</CommandItem>
												)
											})}
										</VirtualizedVirtualizer>
									</CommandGroup>
								</CommandList>
							</Virtualized>
							<Separator />
							<div className='flex justify-between p-2'>
								<Button
									onClick={() => setSelected([])}
									size='sm'
									disabled={!selected.length}
								>
									{t('general_clear')}
								</Button>
								<Button
									onClick={handleApply}
									variant='primary'
									size='sm'
									disabled={isApplyDisabled}
								>
									{t('general_apply')}
								</Button>
							</div>
						</Command>
					</DropdownMenuContent>
				</DropdownMenu>
			</InputGroupAddon>
		</InputGroup>
	)
}
